import * as i0 from '@angular/core';
import { Injectable, Inject, Directive, Input, ErrorHandler, InjectionToken, NgModule, Optional, SkipSelf } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { CommonModule } from '@angular/common';

class AppInsightsService {
    constructor(appInsightsEnv) {
        this.appInsightsEnv = appInsightsEnv;
        this.trackedElements = [];
        this.defaultCustomProperties = {};
        if (this.appInsightsEnv.appInsightsInstrumentationKey !== 'null') {
            this.appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey: this.appInsightsEnv.appInsightsInstrumentationKey,
                    enableAutoRouteTracking: true,
                    enableCorsCorrelation: true,
                    correlationHeaderDomains: [
                        this.appInsightsEnv.appInsightsCorrelationDomains
                    ],
                }
            });
            this.appInsights.loadAppInsights();
            this.appInsights.trackEvent({ name: 'Web app loaded' });
        }
    }
    // track all click events in the DOM. Warning: not tested
    autoTrack(el = window, events = ['click', 'focus', 'blur', 'hover', 'keypress', 'mouseenter', 'mouseleave'], callback) {
        for (const event of events) {
            el.addEventListener(event, callback, true);
        }
        this.trackedElements.push({ el, events, callback });
    }
    removeListeners() {
        this.trackedElements.forEach(({ el, events, callback }) => {
            for (const event of events) {
                el.removeEventListener(event, callback, true);
            }
        });
    }
    setUserId(userId, accountId, storeInCookie) {
        this.appInsights?.setAuthenticatedUserContext(userId, accountId, storeInCookie);
    }
    clearUserId() {
        this.appInsights?.clearAuthenticatedUserContext();
    }
    logPageView(name, uri) {
        this.appInsights?.trackPageView({ name, uri });
    }
    trackError(error) {
        const telemetry = {
            exception: error
        };
        this.appInsights?.trackException(telemetry);
    }
    trackEvent(event, customProperties = {}) {
        this.appInsights?.trackEvent(event, { ...customProperties, ...this.defaultCustomProperties });
    }
    /**
     * Sets the default properties for all events tracked by the app insights service.
     * @param properties - a key value pair of custom properties to be added to the tracked events
     */
    setDefaultCustomProperties(properties) {
        this.defaultCustomProperties = properties;
    }
}
AppInsightsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsService, deps: [{ token: 'appInsightsEnv' }], target: i0.ɵɵFactoryTarget.Injectable });
AppInsightsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['appInsightsEnv']
                }] }]; } });

class TrackAllDirective {
    constructor(elRef, appInsights) {
        this.elRef = elRef;
        this.appInsights = appInsights;
        this.ovtProperties = {};
        this.trackEvent = (event) => {
            const name = this.ovtAction || '';
            const action = { name };
            const properties = {
                ...this.ovtProperties,
                event: event.type
            };
            if (this.ovtCategory) {
                properties.category = this.ovtCategory;
            }
            if (this.ovtLabel) {
                properties.label = this.ovtLabel;
            }
            if (this.ovtValue) {
                properties.value = this.ovtValue;
            }
            this.appInsights.trackEvent(action, properties);
        };
    }
    ngAfterContentInit() {
        this.appInsights.autoTrack(this.elRef.nativeElement, this.ovtEvents, this.trackEvent);
    }
    ngOnDestroy() {
        this.appInsights.removeListeners();
    }
}
TrackAllDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TrackAllDirective, deps: [{ token: i0.ElementRef }, { token: AppInsightsService }], target: i0.ɵɵFactoryTarget.Directive });
TrackAllDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.9", type: TrackAllDirective, selector: "[ovtTrackAll]", inputs: { ovtTrackAll: "ovtTrackAll", ovtEvents: "ovtEvents", ovtAction: "ovtAction", ovtCategory: "ovtCategory", ovtLabel: "ovtLabel", ovtValue: "ovtValue", ovtProperties: "ovtProperties" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TrackAllDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ovtTrackAll]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AppInsightsService }]; }, propDecorators: { ovtTrackAll: [{
                type: Input
            }], ovtEvents: [{
                type: Input
            }], ovtAction: [{
                type: Input
            }], ovtCategory: [{
                type: Input
            }], ovtLabel: [{
                type: Input
            }], ovtValue: [{
                type: Input
            }], ovtProperties: [{
                type: Input
            }] } });

class AnalyticsDirective {
    constructor(elRef, appInsights, renderer) {
        this.elRef = elRef;
        this.appInsights = appInsights;
        this.renderer = renderer;
        this.ovtProperties = {};
    }
    ngAfterContentInit() {
        if (!this.ovtTrack) {
            this.ovtTrack = 'click';
        }
        this.renderer.listen(this.elRef.nativeElement, this.ovtTrack, (event) => this.trackEvent(event));
        if (!this.ovtAction) {
            this.ovtAction = `${this.elRef.nativeElement.localName}.${this.ovtTrack}`;
        }
    }
    trackEvent(event) {
        const name = this.ovtAction || '';
        const action = { name };
        const properties = {
            ...this.ovtProperties,
            event: event.type
        };
        if (this.ovtCategory) {
            properties.category = this.ovtCategory;
        }
        if (this.ovtLabel) {
            properties.label = this.ovtLabel;
        }
        if (this.ovtValue) {
            properties.value = this.ovtValue;
        }
        this.appInsights.trackEvent(action, properties);
    }
}
AnalyticsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AnalyticsDirective, deps: [{ token: i0.ElementRef }, { token: AppInsightsService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
AnalyticsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.9", type: AnalyticsDirective, selector: "[ovtTrack]", inputs: { ovtTrack: "ovtTrack", ovtAction: "ovtAction", ovtCategory: "ovtCategory", ovtLabel: "ovtLabel", ovtValue: "ovtValue", ovtProperties: "ovtProperties" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AnalyticsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ovtTrack]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AppInsightsService }, { type: i0.Renderer2 }]; }, propDecorators: { ovtTrack: [{
                type: Input
            }], ovtAction: [{
                type: Input
            }], ovtCategory: [{
                type: Input
            }], ovtLabel: [{
                type: Input
            }], ovtValue: [{
                type: Input
            }], ovtProperties: [{
                type: Input
            }] } });

class AppInsightsErrorHandler extends ErrorHandler {
    constructor(appInsights) {
        super();
        this.appInsights = appInsights;
    }
    handleError(error) {
        this.appInsights.trackError(error);
        ErrorHandler.prototype.handleError.call(this, error);
    }
}
AppInsightsErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsErrorHandler, deps: [{ token: AppInsightsService }], target: i0.ɵɵFactoryTarget.Injectable });
AppInsightsErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsErrorHandler, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AppInsightsService }]; } });

const APP_INSIGHTS_ENV = new InjectionToken('appInsightsEnv');
class AppInsightsModule {
    /**
     * Creates an instance of the AppInsightsModule while prevents multiple instances of this module from being loaded
     * Read more here: [How can I tell if an NgModule or service was previously loaded?](https://angular.io/guide/ngmodule-faq#how-can-i-tell-if-an-ngmodule-or-service-was-previously-loaded)
     * @param parentModule
     */
    constructor(parentModule) { }
    static forRoot({ appInsightsEnv }) {
        return {
            ngModule: AppInsightsModule,
            providers: [
                { provide: APP_INSIGHTS_ENV, useValue: appInsightsEnv },
                { provide: ErrorHandler, useClass: AppInsightsErrorHandler },
                AppInsightsService,
            ],
        };
    }
}
AppInsightsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsModule, deps: [{ token: AppInsightsModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
AppInsightsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsModule, declarations: [AnalyticsDirective, TrackAllDirective], imports: [CommonModule], exports: [AnalyticsDirective, TrackAllDirective] });
AppInsightsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AppInsightsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [AnalyticsDirective, TrackAllDirective],
                    declarations: [AnalyticsDirective, TrackAllDirective],
                }]
        }], ctorParameters: function () { return [{ type: AppInsightsModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/*
 * Public API Surface of app-insights
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_INSIGHTS_ENV, AnalyticsDirective, AppInsightsErrorHandler, AppInsightsModule, AppInsightsService, TrackAllDirective };
